import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./VideoDetail.scss";
import { BASE_URL } from "../services/constant";
import { fetchEpisodeStudioDetail } from "../services/apiCatalogAdmin";

const VERSION_LABELS = {
  v0: "v0 : 240p - 400kbps",
  v1: "v1 : 480p - 1200kbps",
  v2: "v2 : 720p - 2500kbps",
};

function formatDuration(seconds) {
  if (!seconds) return "00:00";
  const safe = Number(seconds);
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = Math.floor(safe % 60);
  return h > 0
    ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildStudioImage(path) {
  if (!path) return `${BASE_URL}/images/bg.png`;
  if (path.startsWith("/uploads") || path.startsWith("/api/")) {
    return `${BASE_URL}${path}`;
  }
  return `${BASE_URL}api/${path}`;
}

function EpisodeDetail() {
  const { id } = useParams();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ v0: 0, v1: 0, v2: 0 });
  const [status, setStatus] = useState({ v0: false, v1: false, v2: false });
  const [converting, setConverting] = useState({ v0: false, v1: false, v2: false });

  const loadEpisode = async () => {
    try {
      const data = await fetchEpisodeStudioDetail(id);
      setEpisode(data);
      setStatus({
        v0: Number(data?.v0 || 0) === 1,
        v1: Number(data?.v1 || 0) === 1,
        v2: Number(data?.v2 || 0) === 1,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEpisode();
  }, [id]);

  const anyConverting = Object.values(converting).some(Boolean);
  const allConverted = status.v0 && status.v1 && status.v2;

  const handleConvert = async (version) => {
    if (!episode) return;

    setProgress((current) => ({ ...current, [version]: 0 }));
    setStatus((current) => ({ ...current, [version]: false }));
    setConverting((current) => ({ ...current, [version]: true }));

    const evt = new EventSource(`${BASE_URL}api/episode-studio/transcode-progress/${id}/${version}`);

    evt.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setProgress((current) => ({ ...current, [version]: data.progress }));

      if (data.status === "done") {
        setStatus((current) => ({ ...current, [version]: true }));
        setConverting((current) => ({ ...current, [version]: false }));
        evt.close();
      }

      if (data.status === "error") {
        setConverting((current) => ({ ...current, [version]: false }));
        evt.close();
        alert("Erreur lors de la conversion de l'episode.");
      }
    };

    await fetch(`${BASE_URL}api/episode-studio/transcode/${id}/${version}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        relativeSource: episode.video_path,
        duration: episode.duration,
      }),
    });
  };

  const publishEpisodeMaster = async () => {
    const response = await fetch(`${BASE_URL}api/episode-studio/transcode/master/${id}/publish`, {
      method: "POST",
    });

    const payload = await response.json();
    if (payload.success) {
      alert("Master HLS episode publie dans le studio.");
      loadEpisode();
      return;
    }

    alert("Erreur lors de la publication HLS de l'episode.");
  };

  if (loading || !episode) {
    return <div className="loader">Loading...</div>;
  }

  return (
    <div className="page-content">
      <div className="video-detail">
        <h2>Episode details</h2>

        <div className="video-info">
          <div className="video-info-box">
            <img src={buildStudioImage(episode.video_img_1 || episode.video_img_2 || episode.image)} alt="" className="poster" />

            <div className="info">
              <h1>{episode.title}</h1>

              <ul>
                <li><b>ID:</b> {episode.id}</li>
                <li><b>Serie:</b> {episode.series_title}</li>
                <li><b>Saison:</b> {episode.season_title || episode.season_number}</li>
                <li><b>Episode:</b> {episode.episode_number}</li>
                <li><b>Duree:</b> {formatDuration(episode.duration)}</li>
                <li><b>Date:</b> {formatDate(episode.created_at)}</li>
                <li><b>Type:</b> {episode.series_type === "documentaire" ? "Documentaire" : "Serie"}</li>
                <li><b>Categorie:</b> {episode.title_categorie || "-"}</li>
                <li><b>Description:</b></li>
                <li><p>{episode.description}</p></li>
              </ul>
            </div>
          </div>
        </div>

        <h3 className="section-title">Episode conversion</h3>

        <table className="convert-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {["v0", "v1", "v2"].map((version) => (
              <tr key={version}>
                <td>{VERSION_LABELS[version]}</td>
                <td>{status[version] ? <span className="ok">✔ Converted</span> : <span className="no">✖ Not converted</span>}</td>
                <td>
                  <div className="progress-bar">
                    <div className="progress" style={{ width: `${progress[version]}%` }} />
                    {progress[version]}%
                  </div>
                </td>
                <td>
                  <button
                    disabled={status[version] || converting[version] || anyConverting}
                    className={`btn-convert ${converting[version] ? "disabled" : ""}`}
                    onClick={() => handleConvert(version)}
                  >
                    {converting[version] ? <span className="loader-btn"> ... </span> : "Convert"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "50px", textAlign: "center" }}>
          <button
            className="btn-publish"
            disabled={!allConverted}
            onClick={publishEpisodeMaster}
            style={{
              padding: "10px 20px",
              background: allConverted ? "#2ecc71" : "#7f8c8d",
              border: "none",
              color: "#fff",
              cursor: allConverted ? "pointer" : "not-allowed",
              borderRadius: "5px",
              fontSize: "15px",
            }}
          >
            Publier en HLS
          </button>
        </div>
      </div>
    </div>
  );
}

export default EpisodeDetail;
